import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { PrimaryButton } from '../../components/PrimaryButton';
import { SecondaryButton } from '../../components/SecondaryButton';
import {
  createProConnectDashboardLink,
  createProConnectOnboardingLink,
  createProConnectUpdateLink,
  getProConnectStatus,
  type ProConnectStatus,
} from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

/**
 * /professional/billing-subscription used to host a raw bank-details
 * form (account holder / number / IBAN / country) that POSTed to our
 * /professional/me endpoint. That meant pro bank PII transited our
 * backend and lived in masked form on our DB — a compliance gap
 * surfaced by the 2026-06-04 stakeholder review.
 *
 * Replaced with a deferral to Stripe Connect (Express, hosted
 * onboarding). The pro sees their current payout readiness here, then
 * jumps to Stripe's hosted page to enter / update bank details on
 * Stripe's own domain. We never see the account number.
 */
export default function ProBillingSubscriptionPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ProConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getProConnectStatus()
      .then((res) => {
        if (cancelled) return;
        setStatus(res.info);
      })
      .catch(() => {
        // Pro might not have a Connect account yet — `status` stays
        // null and the UI shows the "Start payout setup" branch.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleUpdateBank = async () => {
    setRedirecting(true);
    try {
      // account_update link lands the pro directly on the bank-detail
      // edit screen on Stripe instead of re-running the whole KYC.
      const res = await createProConnectUpdateLink();
      if (res.url) {
        window.location.assign(res.url);
        return;
      }
      toast.error('Could not open Stripe. Please try again.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not open Stripe.'));
    } finally {
      setRedirecting(false);
    }
  };

  const handleStartOnboarding = () => {
    navigate('/professional/setup-payouts');
  };

  const handleStripeDashboard = async () => {
    setRedirecting(true);
    try {
      const res = await createProConnectDashboardLink();
      if (res.url) {
        window.location.assign(res.url);
        return;
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not open Stripe dashboard.'));
    } finally {
      setRedirecting(false);
    }
  };

  const isOnboarded = !!status && status.stripe_account_status !== 'NOT_STARTED';
  const isReady = status?.payouts_ready === true;
  const outstanding = status?.currently_due ?? [];

  return (
    <div className="min-h-screen bg-white flex flex-col font-['DM_Sans',sans-serif]">
      <HeaderPro />

      <main className="flex-1 max-w-[760px] mx-auto w-full px-4 py-6 md:py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px]">
            Payouts &amp; bank
          </h1>
          <Link
            to="/professional/account-settings"
            className="text-[#908e8e] text-[13px] hover:underline"
          >
            ← Back to settings
          </Link>
        </div>

        {loading ? (
          <p className="text-[#908e8e] text-[14px]">Loading…</p>
        ) : (
          <div className="border border-[#ede8e9] rounded-[12px] p-6 bg-white">
            <p className="font-bold text-[#3a3335] text-[14px] mb-2">
              Stripe Connect
            </p>
            <p className="text-[#908e8e] text-[12px] leading-[18px] mb-5">
              Cherry Picked pays you through Stripe. Your bank account and
              identity details are entered, stored and verified by Stripe —
              we never see them. Use the buttons below to set up or update
              your payout account.
            </p>

            <div className="flex items-center justify-between gap-4 mb-5 py-3 border-y border-[#f4eded]">
              <div>
                <p className="text-[12px] text-[#908e8e]">Status</p>
                <p className="text-[14px] font-bold text-[#3a3335]">
                  {isReady
                    ? 'Payouts ready'
                    : isOnboarded
                      ? status?.stripe_account_status === 'PENDING'
                        ? 'Pending verification'
                        : 'Action needed'
                      : 'Not set up'}
                </p>
              </div>
              {isReady ? (
                <span className="text-[12px] font-bold px-2 py-1 rounded bg-[#e6f7ec] text-[#1f7a3a]">
                  ✓ Ready
                </span>
              ) : isOnboarded ? (
                <span className="text-[12px] font-bold px-2 py-1 rounded bg-[#fff4e5] text-[#a86200]">
                  Outstanding
                </span>
              ) : (
                <span className="text-[12px] font-bold px-2 py-1 rounded bg-[#f4eded] text-[#908e8e]">
                  Not started
                </span>
              )}
            </div>

            {outstanding.length > 0 ? (
              <div className="mb-5">
                <p className="text-[12px] font-bold text-[#3a3335] mb-2">
                  Stripe still needs:
                </p>
                <ul className="text-[12px] text-[#3a3335] list-disc pl-5 space-y-0.5">
                  {outstanding.map((item) => (
                    <li key={item}>{humanise(item)}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              {!isOnboarded ? (
                <PrimaryButton
                  onClick={handleStartOnboarding}
                  disabled={redirecting}
                >
                  Start payout setup
                </PrimaryButton>
              ) : (
                <>
                  <PrimaryButton
                    onClick={handleUpdateBank}
                    disabled={redirecting}
                  >
                    {redirecting ? 'Opening Stripe…' : 'Update bank details'}
                  </PrimaryButton>
                  {isReady ? (
                    <SecondaryButton
                      onClick={handleStripeDashboard}
                      disabled={redirecting}
                    >
                      Open Stripe dashboard
                    </SecondaryButton>
                  ) : (
                    <SecondaryButton onClick={handleStartOnboarding}>
                      Continue onboarding
                    </SecondaryButton>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function humanise(code: string): string {
  return code
    .replace(/^individual\./, '')
    .replace(/_/g, ' ')
    .replace(/\./g, ' › ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
